import {
  Component,
  DestroyRef,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

import {
  fuentesFoto,
  ImagenPublica,
  OPERACION_LABELS,
  PropiedadPublica,
  textoODefault,
} from '../../core/models/propiedad.model';
import { Icono } from '../../shared/icono/icono';

/**
 * Hoja A4 de la ficha descargable (FP-25, Figma 349:1162).
 *
 * Vive oculta dentro de la ficha y es lo único visible al imprimir: el botón
 * `Descargar` llama a `window.print()` y el diálogo del navegador la guarda
 * como PDF. Las reglas globales que apagan el resto de la app al imprimir
 * están en `styles.scss`.
 *
 * Encabezado con el logo del broker titular (apaisado) y el contacto de la
 * inmobiliaria —su nombre, o el del broker titular si no lo cargó, con el
 * WhatsApp del broker (issue #58 del backoffice)—; pie con la marca Phindi.
 * Los precios, la dirección y la viñeta llegan ya resueltos desde la ficha
 * para no duplicar reglas de negocio.
 */
@Component({
  selector: 'app-ficha-pdf',
  imports: [Icono],
  templateUrl: './ficha-pdf.html',
  styleUrl: './ficha-pdf.scss',
})
export class FichaPdf {
  readonly propiedad = input.required<PropiedadPublica>();
  readonly galeria = input.required<ImagenPublica[]>();
  /** Precios ya formateados por la ficha (null = no aplica a la operación). */
  readonly precioVenta = input.required<string | null>();
  readonly precioAlquiler = input.required<string | null>();
  /** Dirección completa de la sección Zona (`direccion, localidad`). */
  readonly direccion = input.required<string>();
  /** Logo del broker titular, ya filtrado por `esUrlRenderizable`. */
  readonly logo = input.required<string | null>();
  readonly inmobiliaria = input.required<string | null>();
  /**
   * Link compartido de una alianza (#17): la hoja sale sin los datos del
   * broker —logo, nombre de la inmobiliaria, WhatsApp, teléfono ni agente—.
   */
  readonly ocultarContacto = input(false);

  readonly operacion = computed(
    () => OPERACION_LABELS[this.propiedad().operacion],
  );

  /** WhatsApp de la hoja: siempre el del broker titular (lo manda el backend). */
  readonly whatsapp = computed(() =>
    textoODefault(this.propiedad().contacto?.whatsapp),
  );

  /**
   * Nombre con el que firma la hoja, en el encabezado y junto al WhatsApp: el
   * de la inmobiliaria o, si no lo cargó, el del broker titular (issue #58).
   * `contacto.agente` lleva el nombre del broker pese a su nombre histórico.
   * El backend ya cae al broker con `empresa` null; se repite acá para una
   * `empresa` en blanco.
   */
  readonly nombreContacto = computed(
    () =>
      this.inmobiliaria() ?? textoODefault(this.propiedad().contacto?.agente),
  );

  /** Foto grande de la izquierda (variante `card`, 800 px). */
  readonly fotoPrincipal = computed(() => {
    const [portada] = this.galeria();
    return portada ? fuentesFoto(portada, 'card').src : null;
  });

  /**
   * Las 4 celdas de la grilla derecha (variante `thumb`, 400 px). Siempre son
   * 4: sin foto la celda queda como placeholder gris, igual que en el frame.
   */
  readonly fotosGrilla = computed(() => {
    const resto = this.galeria().slice(1, 5);
    return Array.from({ length: 4 }, (_, i) =>
      resto[i] ? fuentesFoto(resto[i], 'thumb').src : null,
    );
  });

  readonly anio = new Date().getFullYear();

  /**
   * Fecha y hora de generación (`24/07/2026 - 12:30 pm`). Se refresca en
   * `beforeprint`, así también queda al día si se imprime con Ctrl+P.
   */
  readonly fecha = signal(formatearFechaGeneracion(new Date()));

  constructor() {
    // La ficha se renderiza en el servidor, donde no hay `window`:
    // el listener se registra recién en el navegador.
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const alImprimir = () =>
        this.fecha.set(formatearFechaGeneracion(new Date()));
      window.addEventListener('beforeprint', alImprimir);
      destroyRef.onDestroy(() =>
        window.removeEventListener('beforeprint', alImprimir),
      );
    });
  }

  /** Una imagen que falla se oculta: queda la celda gris, no el ícono roto. */
  ocultarRota(evento: Event): void {
    (evento.target as HTMLElement).style.display = 'none';
  }
}

/** `24/07/2026 - 12:30 pm`, como lo muestra el frame (sin locale mediante). */
function formatearFechaGeneracion(fecha: Date): string {
  const dos = (n: number) => String(n).padStart(2, '0');
  const horas = fecha.getHours() % 12 || 12;
  const meridiano = fecha.getHours() < 12 ? 'am' : 'pm';
  return (
    `${dos(fecha.getDate())}/${dos(fecha.getMonth() + 1)}/${fecha.getFullYear()}` +
    ` - ${horas}:${dos(fecha.getMinutes())} ${meridiano}`
  );
}
